import parse from 'html-react-parser'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { FRONT_BASE_URL } from '@/constants'

import { WPPageData } from '@/types'

type MetaProps = {
  pageData: WPPageData
}

const Meta = ({ pageData }: MetaProps) => {
  const { asPath, locale } = useRouter()
  const path = asPath.split(/[?#]/)[0].replace(/\/?$/, '/')
  const canonical = `${FRONT_BASE_URL}${
    locale && locale !== 'default' ? `/${locale}` : ''
  }${path}`
  const yoastHead = pageData?.yoast_head || ''
  const hasCanonical = yoastHead.includes('rel="canonical"')
  // WP's own site URL (no "www") leaks into absolute og:url/twitter:url values,
  // since the regex below only rewrites relative "/..." hrefs
  const backendOrigin = FRONT_BASE_URL!.replace('://www.', '://')

  const translations = pageData?.wpml_translations || []

  const robotsContent = [
    pageData?.meta_robots?.is_robots_noindex ? 'noindex' : 'index',
    pageData?.meta_robots?.is_robots_nofollow ? 'nofollow' : 'follow'
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <Head>
      {parse(
        yoastHead
          .replace(/"\//g, `"${FRONT_BASE_URL}/`)
          .replaceAll(backendOrigin, FRONT_BASE_URL!)
      )}
      {hasCanonical ? null : <link rel="canonical" href={canonical} />}
      {translations.map((t) => (
        <link
          key={t.code}
          rel="alternate"
          hrefLang={t.code}
          href={`${FRONT_BASE_URL}${t.href}`}
        />
      ))}
      {(() => {
        // x-default must be a final URL (middleware redirects "/" by browser
        // language), so reuse the "en" alternate of the current page
        const xDefault = translations.find((t) => t.code === 'en')
        return xDefault ? (
          <link
            rel="alternate"
            hrefLang="x-default"
            href={`${FRONT_BASE_URL}${xDefault.href}`}
          />
        ) : null
      })()}
      {robotsContent ? <meta name="robots" content={robotsContent} /> : null}
    </Head>
  )
}

export default Meta
